import _ from "lodash";
import PlayoutService from "@/services/PlayoutService";
import moment from "moment";

const defaultState = () => ({
  playoutConfig: null,
  startTime: null,
  isExist: false,
  // Tracks unsaved schedule changes globally so the "Set Schedule" button can
  // reflect edits made while the schedule controls are unmounted (e.g. adding a
  // file from the Files tab).
  scheduleDirty: false,
  // Configs already fetched by the dashboard cards, keyed by playout id, so the
  // single playout page can render the schedule before its own request returns.
  configCache: {},
  // Bumped on every server load so watchers can tell a sync from a user edit.
  serverSyncSeq: 0,
});

const CONFIG_CACHE_TTL_MS = 10 * 60 * 1000;

const cloneConfigForCache = (config) => {
  const copy = _.cloneDeep(config);
  if (Array.isArray(copy?.playlist)) {
    copy.playlist.forEach((item) => {
      if (item) delete item._uid;
    });
  }
  return copy;
};

// Stable, client-only unique id for each playlist item. The persisted `index`
// field is NOT unique (e.g. the same livestream added twice shares its key),
// which caused duplicate Vue keys and phantom duplicate rows while dragging.
// `_uid` guarantees a unique, stable key per item across reorders.
let _uidCounter = 0;
const generateUid = () =>
  `pl_${Date.now().toString(36)}_${(_uidCounter++).toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const ensurePlaylistUids = (playlist) => {
  if (!Array.isArray(playlist)) return;
  playlist.forEach((item) => {
    if (item && !item._uid) item._uid = generateUid();
  });
};

const state = defaultState();

const mutations = {
  GET_PLAYOUT_CONFIG(state, playoutConfig) {
    state.serverSyncSeq += 1;
    // Preserve unsaved schedule edits. A config refresh triggered by an
    // upload/delete (to update usage totals) must not wipe temporary playlist
    // items the user added but hasn't persisted via "Set Schedule" yet.
    if (state.scheduleDirty && state.playoutConfig?.playlist) {
      const preservedPlaylist = state.playoutConfig.playlist;
      ensurePlaylistUids(preservedPlaylist);
      state.playoutConfig = { ...playoutConfig, playlist: preservedPlaylist };
      return;
    }
    state.playoutConfig = playoutConfig;
    if (!Array.isArray(state.playoutConfig.playlist)) {
      state.playoutConfig.playlist = [];
    }
    // Backfill unique keys for items loaded from the backend (which only carry
    // the non-unique `index` field).
    ensurePlaylistUids(state.playoutConfig.playlist);
    if (
      state.playoutConfig?.playlist[0]?.startTime &&
      state.playoutConfig?.status !== "ended"
    ) {
      state.startTime = state.playoutConfig?.playlist[0]?.startTime;
      state.isExist = true;
    } else if (
      state.playoutConfig?.playlist[0]?.startTime &&
      state.playoutConfig?.status === "ended"
    ) {
      state.startTime = moment().add(3, "minutes").valueOf();
      const timeDiff =
        state.startTime - state.playoutConfig?.playlist[0]?.startTime;
      state.playoutConfig?.playlist.map((element) => {
        element.startTime = element.startTime + timeDiff;
        element.endTime = element.endTime + timeDiff;
      });
      state.isExist = true;
    } else {
      state.startTime = moment().add(3, "minutes").valueOf();
    }
    // Freshly loaded config has no unsaved changes.
    state.scheduleDirty = false;
  },
  ADD_ITEM(state, item) {
    const index = state.playoutConfig.playlist?.length;
    const LIVESTREAM_DEFAULT_DURATION = 600000; // 10 min
    const BREAK_DEFAULT_DURATION = 120000; // 2 min
    const playlistElement = { _uid: generateUid() };
    const latestElement = state.playoutConfig.playlist
      .sort((a, b) => {
        new Date(b.endTime) - new Date(a.endTime);
      })
      .slice(-1)
      .pop();

    playlistElement.startTime =
      state.playoutConfig.playlist?.length > 0
        ? latestElement.endTime + 1000
        : (playlistElement.startTime = state.startTime);

    if (item) {
      playlistElement.endTime =
        item.type === "live"
          ? playlistElement.startTime + LIVESTREAM_DEFAULT_DURATION
          : moment(playlistElement.startTime)
              .add(item.mediaInfo.duration, "seconds")
              .valueOf();

      playlistElement.sourceName =
        item.type === "live" ? item.name : item.fileName;
      playlistElement.sourceLocation =
        item.type === "live" ? item.key : item._id;
      playlistElement.sourceType =
        item.type === "live"
          ? "live"
          : item.type === "playout-upload"
            ? "playout-upload"
            : "vod";
      playlistElement.index =
        item.type === "live" ? item.key : item.id + "-" + index;
    } else {
      playlistElement.endTime = moment(playlistElement.startTime)
        .add(BREAK_DEFAULT_DURATION, "milliseconds")
        .valueOf();
      playlistElement.sourceName = "Break";
      playlistElement.sourceLocation = "break";
      playlistElement.sourceType = "break";
      playlistElement.index = "break" + index;
    }

    state.playoutConfig.playlist.push(playlistElement);
    // Adding a source (from either the schedule "Add source" modal or the Files
    // tab) is an unsaved change that should enable "Set Schedule".
    state.scheduleDirty = true;
  },
  DELETE_ITEM(state, item) {
    const itemIndex = state.playoutConfig.playlist.indexOf(item);
    const timeDiff = item.endTime - item.startTime + 1000;
    state.playoutConfig.playlist.splice(itemIndex, 1);
    state.playoutConfig.playlist.map((element) => {
      const currentIndex = state.playoutConfig.playlist.indexOf(element);
      if (currentIndex >= itemIndex) {
        element.startTime = element.startTime - timeDiff;
        element.endTime = element.endTime - timeDiff;
      }
    });
  },
  CHANGE_START_TIME(state, item) {
    const newStartTime = moment(item).valueOf();
    if (state.playoutConfig?.playlist?.length > 0) {
      const currentFirstStart =
        state.playoutConfig.playlist[0].startTime || state.startTime;
      const timeDiff = newStartTime - currentFirstStart;
      state.playoutConfig.playlist.forEach((element) => {
        element.startTime = element.startTime + timeDiff;
        element.endTime = element.endTime + timeDiff;
      });
    }
    state.startTime = newStartTime;
  },
  CHANGE_ITEM_DURATION(state, data) {
    const { item, value } = data;
    const itemIndex = state.playoutConfig.playlist.indexOf(item);
    const duration = moment.duration(value).asMilliseconds();
    const oldEndTime = state.playoutConfig.playlist[itemIndex].endTime;
    const newEndTime =
      state.playoutConfig.playlist[itemIndex].startTime + duration;
    const timeDiff = newEndTime - oldEndTime;
    item.endTime = item.endTime + timeDiff;
    state.playoutConfig.playlist.map((element) => {
      const currentIndex = state.playoutConfig.playlist.indexOf(element);
      if (currentIndex > itemIndex) {
        element.startTime = element.startTime + timeDiff;
        element.endTime = element.endTime + timeDiff;
      }
    });
  },
  CHANGE_ITEM_END_TIME(state, data) {
    const { item, value } = data;
    const itemIndex = state.playoutConfig.playlist.indexOf(item);
    const oldEndTime = state.playoutConfig.playlist[itemIndex].endTime;
    const newEndTime = moment(value).valueOf();
    console.log(newEndTime);
    const timeDiff = newEndTime - oldEndTime;
    item.endTime = newEndTime;
    state.playoutConfig.playlist.map((element) => {
      const currentIndex = state.playoutConfig.playlist.indexOf(element);
      if (currentIndex > itemIndex) {
        element.startTime = element.startTime + timeDiff;
        element.endTime = element.endTime + timeDiff;
      }
    });
  },
  SET_DESTINATION(state, id) {
    state.playoutConfig.destination = id;
  },
  SET_MODE(state, mode) {
    state.playoutConfig.mode = mode;
    state.playoutConfig.playlist = [];
  },
  SET_LOOP_MODE(state, endDate) {
    endDate
      ? ((state.playoutConfig.settings.loopMode = true),
        (state.playoutConfig.settings.loopEndDate = moment(endDate).valueOf()))
      : ((state.playoutConfig.settings.loopMode = false),
        (state.playoutConfig.settings.loopEndDate = null));
  },
  TOGGLE_DESTINATION(state, status) {
    !state.playoutConfig.settings?.destinationEnabled
      ? (state.playoutConfig.settings.destinationEnabled = true)
      : (state.playoutConfig.settings.destinationEnabled = false);

    if (_.isBoolean(status))
      state.playoutConfig.settings.destinationEnabled = status;
  },
  TOGGLE_RETAIN_CONTENTS(state) {
    if (!state.playoutConfig.settings) {
      state.playoutConfig.settings = {};
    }
    state.playoutConfig.settings.retainContents =
      !state.playoutConfig.settings.retainContents;
  },
  SET_SCHEDULE_RESULT(state, isExist) {
    state.isExist = isExist;
    // Schedule was just persisted — clear unsaved-changes flag.
    state.scheduleDirty = false;
  },
  SET_ADVANCED_SETTINGS(state, { fps, resolution }) {
    if (fps) state.playoutConfig.settings.fps = fps;
    if (resolution) state.playoutConfig.settings.resolution = resolution;
  },
  SET_NEW_ORDER(state, { oldIndex, newIndex }) {
    const playlist = state.playoutConfig?.playlist;
    if (!playlist?.length) return;

    // Guard against out-of-range / no-op moves so we never corrupt the list.
    if (
      oldIndex == null ||
      newIndex == null ||
      oldIndex < 0 ||
      newIndex < 0 ||
      oldIndex >= playlist.length ||
      newIndex >= playlist.length ||
      oldIndex === newIndex
    ) {
      return;
    }

    // The UI renders items via the Playout/playlist getter, which sorts a copy
    // ascending by startTime. The drag indices (oldIndex/newIndex) are computed
    // against THAT order, so we must reorder against the same ascending order —
    // not the raw storage order — otherwise the wrong item gets moved.
    const ordered = [...playlist].sort((a, b) => a.startTime - b.startTime);

    // Anchor the timeline to the earliest start time so the schedule start is
    // preserved regardless of which item ends up first after the move.
    const playoutStart = ordered[0].startTime;

    // Move the dragged item into its new slot.
    const [draggableItem] = ordered.splice(oldIndex, 1);
    ordered.splice(newIndex, 0, draggableItem);

    // Re-lay the timeline sequentially, keeping each item's own duration and a
    // 1s gap between items. This guarantees start times are strictly increasing,
    // so an item can never end up starting before the previous one.
    let cursor = playoutStart;
    ordered.forEach((element) => {
      const duration = element.endTime - element.startTime;
      element.startTime = cursor;
      element.endTime = cursor + duration;
      cursor = element.endTime + 1000;
    });

    state.playoutConfig.playlist = ordered;
  },
  CACHE_CONFIG(state, { id, data }) {
    if (!id || !data) return;
    state.configCache = {
      ...state.configCache,
      [id]: { data: cloneConfigForCache(data), fetchedAt: Date.now() },
    };
  },
  CLEAR(state) {
    state.playoutConfig = null;
    state.startTime = null;
    state.isExist = false;
    state.scheduleDirty = false;
  },
};

const actions = {
  async getPlayoutConfig({ commit }, id) {
    const playoutConfig = await PlayoutService.getPlayoutConfig(id);
    commit("CACHE_CONFIG", { id, data: playoutConfig });
    commit("GET_PLAYOUT_CONFIG", playoutConfig);
  },
  cacheConfig({ commit }, payload) {
    commit("CACHE_CONFIG", payload);
  },
  seedConfig({ commit }, config) {
    commit("GET_PLAYOUT_CONFIG", _.cloneDeep(config));
  },
  applyFreshConfig({ commit }, { id, config }) {
    commit("CACHE_CONFIG", { id, data: config });
    commit("GET_PLAYOUT_CONFIG", _.cloneDeep(config));
  },
  addItem({ commit }, item) {
    commit("ADD_ITEM", item);
  },
  deleteItem({ commit }, item) {
    commit("DELETE_ITEM", item);
  },
  changeStartTime({ commit }, item) {
    commit("CHANGE_START_TIME", item);
  },
  changeItemDuration({ commit }, data) {
    commit("CHANGE_ITEM_DURATION", data);
  },
  changeItemEndTime({ commit }, data) {
    commit("CHANGE_ITEM_END_TIME", data);
  },
  setDestination({ commit }, id) {
    commit("SET_DESTINATION", id);
  },
  setMode({ commit }, mode) {
    commit("SET_MODE", mode);
    if (mode === "schedule") commit("SET_LOOP_MODE");
  },
  toggleDestination({ commit }, status) {
    commit("TOGGLE_DESTINATION", status);
  },
  toggleRetainContents({ commit }) {
    commit("TOGGLE_RETAIN_CONTENTS");
  },
  async setSchedule({ commit, state, dispatch }, playoutId) {
    // v2 flow always retains sources after a playout finishes (the backend
    // otherwise clears the playlist on "ended" status when this is falsy).
    if (state.playoutConfig) {
      if (!state.playoutConfig.settings) state.playoutConfig.settings = {};
      state.playoutConfig.settings.retainContents = true;
    }
    const response = await PlayoutService.savePlayoutConfig(
      playoutId,
      state.playoutConfig,
    );
    commit("SET_SCHEDULE_RESULT", !!response?.playlist?.[0]?.startTime);
    commit("CACHE_CONFIG", { id: playoutId, data: state.playoutConfig });

    // Keep dashboard cards in sync when navigating back without a refetch.
    const cfg = state.playoutConfig || {};
    dispatch(
      "Streams/patchStream",
      {
        streamId: playoutId,
        patch: {
          config: {
            mode: cfg.mode,
            fps: cfg.settings?.fps ?? cfg.fps,
            resolution: cfg.settings?.resolution ?? cfg.resolution,
          },
        },
      },
      { root: true },
    );
    dispatch(
      "Streams/syncPlayoutDestination",
      { playoutId, destinationId: cfg.destination || null },
      { root: true },
    );
  },
  setAdvancedSettings({ commit }, { fps, resolution }) {
    commit("SET_ADVANCED_SETTINGS", { fps, resolution });
  },
  setNewOrder({ commit }, { oldIndex, newIndex }) {
    commit("SET_NEW_ORDER", { oldIndex, newIndex });
  },
  setLoopMode({ commit }, endDate) {
    commit("SET_LOOP_MODE", endDate);
  },
  clear({ commit }) {
    commit("CLEAR");
  },
};

const getters = {
  playoutConfig: (state) => state.playoutConfig,
  serverSyncSeq: (state) => state.serverSyncSeq,
  cachedConfig: (state) => (id) => {
    const entry = state.configCache[id];
    if (!entry || Date.now() - entry.fetchedAt > CONFIG_CACHE_TTL_MS) return null;
    return entry.data;
  },
  playlist: (state) => {
    const items = state.playoutConfig?.playlist;
    if (!items?.length) return items || [];
    return [...items].sort((a, b) => a.startTime - b.startTime);
  },
  playoutStartTime: (state, getters) =>
    getters.playlist &&
    getters.playlist[0]?.startTime &&
    state.playoutConfig?.status !== "ended"
      ? getters?.playlist[0]?.startTime
      : state.startTime,
  playoutDuration: (state) => {
    let duration = 0;
    state.playoutConfig?.playlist.map((item) => {
      const itemDuration = item.endTime - item.startTime;
      duration += itemDuration;
    });
    return duration;
  },
  isPlayoutInThePast: (state, getters) => {
    return (
      getters.playoutDuration > 0 &&
      getters.playoutStartTime + getters.playoutDuration < moment().valueOf()
    );
  },
  isPlayoutExist: (state) => state.isExist,
  scheduleDirty: (state) => state.scheduleDirty,
  shouldShowCounter: (state) => {
    return state.isExist && state.playoutConfig?.status !== "ended";
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
